import { useCallback } from 'react';

import { useTracking } from '@change-corgi/core/react/tracking';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';

import { updateEndorsement } from '../../api';
import type { EndorsementError } from '../../shared/hooks';
import { useEndorsementForm } from '../../shared/hooks';
import type { EndorsementFormData } from '../../shared/types';

export type { EndorsementError } from '../../shared/hooks';

type UseEditEndorsementProps = {
	endorsementId: string;
	initialData?: Partial<EndorsementFormData>;
	onSuccess?: () => void;
};

function getErrorType(msg: string): EndorsementError['type'] {
	if (msg.includes('authenticated') || msg.includes('401')) return 'UNAUTHORIZED';
	if (msg.includes('400')) return 'VALIDATION';
	return 'SERVER_ERROR';
}

function buildUpdateInput(endorsementId: string, petitionId: string, formData: EndorsementFormData) {
	return {
		endorsementId,
		petitionId,
		name: formData.name || undefined,
		title: formData.title || undefined,
		url: formData.url || undefined,
		content: formData.statement || undefined,
		...(formData.photoUrl && { photo: { url: formData.photoUrl } }),
	};
}

export function useEditEndorsement({ endorsementId, initialData, onSuccess }: UseEditEndorsementProps) {
	const track = useTracking();
	const utilityContext = useUtilityContext();
	const {
		data: { petition },
	} = useStarterDashboardV2PageData();
	const form = useEndorsementForm(initialData);

	const handleSubmit = useCallback(async () => {
		form.setIsSubmitting(true);
		form.setError(null);

		try {
			void track('starter_dashboard_endorsement_edit_submit_click');
			const input = buildUpdateInput(endorsementId, petition.id, form.formData);
			const response = await updateEndorsement(input, utilityContext);

			if (response.success) {
				void track('starter_dashboard_endorsement_update_success');
				onSuccess?.();
			} else {
				const msg = response.errorMessage || 'Failed to update endorsement';
				void track('starter_dashboard_endorsement_update_error', { error: msg });
				form.setError({ message: msg, type: getErrorType(msg) });
			}
		} catch (e) {
			const msg = e instanceof Error ? e.message : 'Unexpected error';
			void track('starter_dashboard_endorsement_update_error', { error: msg });
			form.setError({ message: msg, type: 'NETWORK_ERROR' });
		} finally {
			form.setIsSubmitting(false);
		}
	}, [endorsementId, petition.id, form, utilityContext, track, onSuccess]);

	return {
		data: {
			formData: form.formData,
			isSubmitting: form.isSubmitting,
			statementCharCount: form.statementCharCount,
			statementMaxLength: form.statementMaxLength,
			error: form.error,
		},
		actions: {
			handleFieldChange: form.handleFieldChange,
			handlePhotoUpload: form.handlePhotoUpload,
			handleSubmit,
			clearError: form.clearError,
			resetForm: form.resetForm,
		},
	};
}
